'use client';

import { useEffect, useRef } from 'react';
import { isLighthouseClient } from '@/lib/isLighthouse';
import { pushTaboola, scheduleTaboolaFlush } from './TaboolaWidget';

// Right-rail placement shown after the "விடியோக்கள்" (Videos) section in the
// sticky sidebar. Each scrolled-in article is a new "page", so the container id
// must be unique across the session — we append the article's index to the base.
const PLACEMENT = {
  mode: 'thumbnails-rr',
  base: 'taboola-infinite-right-rail-thumbnails',
  placement: 'Infinite Right Rail Thumbnails'
} as const;
interface Props {
  /** Stable per-article index — makes each scrolled-in page's container id unique. */
  pageIndex: number;
  /** Canonical (non-AMP) URL of this article, passed to Taboola as article:auto context. */
  canonicalUrl: string;
}

/**
 * Renders the Taboola right-rail placement for one article and runs the
 * onboarding sequence exactly once when the article mounts: push the widget
 * (with its unique container), then re-declare `{article:'auto', url}` for this
 * page's canonical URL, then flush. Flush is debounced in TaboolaWidget so a
 * burst of articles scrolling in coalesces into one flush.
 */
export default function TaboolaRightRail({
  pageIndex,
  canonicalUrl
}: Props) {
  const pushedRef = useRef(false);
  const container = `${PLACEMENT.base}-${pageIndex}`;
  useEffect(() => {
    if (pushedRef.current || isLighthouseClient()) return;
    pushedRef.current = true;
    pushTaboola({
      mode: PLACEMENT.mode,
      container,
      placement: PLACEMENT.placement,
      target_type: 'mix'
    });
    pushTaboola({
      article: 'auto',
      url: canonicalUrl
    });
    scheduleTaboolaFlush();
    // pageIndex is fixed for the life of this instance; canonicalUrl identifies the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canonicalUrl]);
  return <div id={container} className="mt-4 px-2 pb-2" />;
}